import React, {
  forwardRef,
  useRef,
  useState,
  FC,
  ForwardRefExoticComponent,
  RefAttributes,
  useEffect,
  MutableRefObject,
} from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { palette } from '@lib/styles/palette';
import pixelToRem from '@lib/styles/pixelToRem';

import Button from '@components/common/Button';
import Icon from '@components/common/Icon';

type OptionsArray = { id: number; value: string }[];

interface SearchFunctionInterface {
  (inputText: string): void;
}

type OptionsType = {
  options: OptionsArray;
  isShow: boolean;
  keyword: string;
  onSelect?: React.MouseEventHandler<HTMLLIElement>;
};
interface AutoCompleteProps {
  value: string;
  options: OptionsArray;
  placeholder: string;
  setSearchRecord?: (keyword: string) => void;
  moveToDetailPricePageOf: (keyword: string) => void;
  setAutoCompleteList?: SearchFunctionInterface;
}
interface ParentComposition {
  SearchList: ForwardRefExoticComponent<
    OptionsType & RefAttributes<HTMLUListElement>
  >;
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

const AutoComplete: ParentComposition & FC<AutoCompleteProps> = ({
  value,
  options,
  placeholder,
  setSearchRecord,
  setAutoCompleteList,
  moveToDetailPricePageOf,
}: AutoCompleteProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isComposing, setIsComposing] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<string>('');

  const AutoCompleteUlRef = useRef<HTMLUListElement>(null);

  // 검색어 입력
  const inputHandler: React.FormEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target as HTMLInputElement;

    setAutoCompleteList?.(value);
    setSelectedItem(value);
    setIsExpanded(true);
  };

  // 검색창 focusout
  const blurHandler: React.FormEventHandler<HTMLInputElement> = () => {
    setIsExpanded(false);
  };

  // 방향키로 탐색
  const keyDownEventHandler: React.KeyboardEventHandler<HTMLInputElement> = (
    e,
  ) => {
    // 한글 등 IME composing 과정에서 keydown 이벤트 방지
    if (isComposing) {
      return;
    }

    const AutoCompleteArrayOfLi: HTMLCollection = (
      AutoCompleteUlRef.current as HTMLUListElement
    ).children;
    const firstAutoCompleteLi = AutoCompleteArrayOfLi[0];
    const lastAutoCompleteLi =
      AutoCompleteArrayOfLi[AutoCompleteArrayOfLi.length - 1];
    // const activeAutoCompleteLi =
    //   (AutoCompleteUlRef.current as HTMLUListElement).querySelector(
    //     '#isSelected',
    //   ) ?? firstAutoCompleteLi;
    let activeAutoCompleteLi = (
      AutoCompleteUlRef.current as HTMLUListElement
    ).querySelector('#isSelected');

    switch (e.key) {
      case 'Down':
      case 'ArrowDown':
        if (!activeAutoCompleteLi) {
          activeAutoCompleteLi = firstAutoCompleteLi;
          activeAutoCompleteLi.id = 'isSelected';
          return;
        }

        if (!activeAutoCompleteLi.nextElementSibling) {
          activeAutoCompleteLi.id = '';
          firstAutoCompleteLi.id = 'isSelected';
          return;
        }

        activeAutoCompleteLi.id = '';
        activeAutoCompleteLi.nextElementSibling.id = 'isSelected';

        break;
      case 'Up':
      case 'ArrowUp':
        e.preventDefault();
        if (!activeAutoCompleteLi) {
          activeAutoCompleteLi = lastAutoCompleteLi;
          activeAutoCompleteLi.id = 'isSelected';
          return;
        }

        if (!activeAutoCompleteLi.previousElementSibling) {
          activeAutoCompleteLi.id = '';
          lastAutoCompleteLi.id = 'isSelected';
          return;
        }

        activeAutoCompleteLi.id = '';
        activeAutoCompleteLi.previousElementSibling.id = 'isSelected';

        break;
      case 'Enter':
        setIsExpanded(false);

        try {
          if (!setSearchRecord) {
            throw new Error(
              "Can't update search record because There isn't setSearchRecord function in AutoComplete",
            );
          }

          const activeElementCotent = activeAutoCompleteLi?.textContent;
          const selectedKeyword = activeElementCotent ?? selectedItem;

          setSearchRecord?.(selectedKeyword);
          setSelectedItem(selectedKeyword);
          moveToDetailPricePageOf(selectedKeyword);
        } catch (e) {
          console.log(e);
        }

        break;
      case 'Esc':
      case 'Escape':
        setIsExpanded(false);
        break;
      default:
        break;
    }
  };

  return (
    <div css={containerStyle}>
      <form css={formStyle} onSubmit={(e) => e.preventDefault()}>
        <label css={a11yHiddenStyle}>{placeholder}</label>
        <input
          css={inputStyle}
          type="text"
          role="combobox"
          defaultValue={value || selectedItem}
          autoComplete="off"
          aria-autocomplete="list"
          placeholder={placeholder}
          aria-activedescendant="isSelected"
          aria-expanded={isExpanded}
          aria-owns="autocomplete-list"
          onFocus={inputHandler}
          onBlur={blurHandler}
          onInput={inputHandler}
          onKeyDown={keyDownEventHandler}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
        />
        <button type="button" aria-label="search" css={buttonStyle}>
          <Icon icon="search" size="20px" />
        </button>
      </form>
      <AutoComplete.SearchList
        options={options}
        ref={AutoCompleteUlRef}
        isShow={isExpanded}
        keyword={selectedItem}
        onSelect={(e: React.MouseEvent<HTMLLIElement>) => {
          const keyword = (e.target as HTMLLIElement).innerText;

          try {
            if (!setSearchRecord) {
              throw new Error(
                "Can't update search record because There isn't setSearchRecord function",
              );
            }

            setSearchRecord?.(keyword);
            setSelectedItem(keyword);
            moveToDetailPricePageOf(keyword);

            setIsExpanded(false);
          } catch (e) {
            console.log(e);
          }
        }}
      />
    </div>
  );
};

AutoComplete.SearchList = forwardRef<HTMLUListElement, OptionsType>(
  function SearchList({ options, isShow, keyword, onSelect }, ref) {
    const isEmpty = options.length === 0;
    const ulRef = useRef<HTMLUListElement | null>(null);

    useEffect(() => {
      if (!ulRef.current) return;

      const childrenOfUl = Array.from(ulRef.current.children);
      const selectedItemIndexArr = childrenOfUl
        .map((element, i) => {
          if (element.id === 'isSelected') return i;
        })
        .filter(isDefined);

      const isThereSeletedItemInUl = selectedItemIndexArr.length > 0;
      const isDuplicateInArray = selectedItemIndexArr.length > 1;

      /** isSelected 로 id가 부여된 아이템이 없을 경우, 가장 처음 아이템을 isSelected로 지정 */
      // if (!isThereSeletedItemInUl && options.length !== 0) {
      //   childrenOfUl[0].id = 'isSelected';
      // }

      /** 검색 리스트가 중복 될 경우, 첫 아이템 제외하고 나머지 선택 제외 */
      if (isDuplicateInArray) {
        selectedItemIndexArr.shift();

        for (const index of selectedItemIndexArr) {
          childrenOfUl[index].id = '';
        }
      }
    });

    return (
      <ul
        css={listStyle(isShow)}
        ref={(element) => {
          ulRef.current = element;

          if (typeof ref === 'function') {
            ref(element);
          } else if (ref) {
            (ref as MutableRefObject<HTMLUListElement | null>).current =
              element;
          }
        }}
        id="autocomplete-list"
      >
        {isEmpty && <li css={itemStyle}>검색결과가 없습니다</li>}
        {options.map(({ id, value }) => {
          const isMatchedKeyword = keyword === value;
          return (
            <li
              css={itemStyle}
              key={id}
              // id={isMatchedKeyword ? 'isSelected' : ''}
              onClick={onSelect}
              onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
            >
              {value}
            </li>
          );
        })}
      </ul>
    );
  },
);

const containerStyle = css`
  position: relative;
  margin-top: ${pixelToRem(28)};
  width: 100%;
  min-width: ${pixelToRem(260)};
  max-width: ${pixelToRem(460)};

  /** mobile */
  @media (max-width: 599px) {
    margin: 0;
    max-width: 100%;
  }

  @media (min-width: 600px) and (max-width: 1023px) {
    margin: 0;
    max-width: 100%;
  }
`;
const formStyle = css`
  display: flex;
  border: 1px solid ${palette.gray2};
  border-radius: ${pixelToRem(26)};
  height: ${pixelToRem(50)};
  align-items: center;
  overflow: hidden;
  padding: ${pixelToRem(15, 12, 15, 20)};
  box-sizing: border-box;
  max-width: ${pixelToRem(460)};
  width: 100%;

  /** mobile */
  @media (max-width: 599px) {
    background-color: ${palette.gray0};
    border-radius: 6px;
    border: 0;
    padding: ${pixelToRem(11, 15)};
    height: 40px;
    flex-direction: row-reverse;
    max-width: 100%;
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    background-color: ${palette.gray0};
    border-radius: 6px;
    border: 0;
    padding: ${pixelToRem(11, 15)};
    height: 40px;
    flex-direction: row-reverse;
    max-width: 100%;
  }
`;

const a11yHiddenStyle = css`
  overflow: hidden;
  position: absolute;
  clip: rect(0, 0, 0, 0);
  clip-path: circle(0);
  width: 1px;
  height: 1px;
  margin: -1px;
  border: 0;
  padding: 0;
  white-space: nowrap;
`;

const inputStyle = css`
  height: auto;
  flex: 1 0 auto;
  border: 0;

  /** mobile */
  @media (max-width: 599px) {
    background-color: ${palette.gray0};
    padding: 0;
    margin-left: 8px;
    margin-top: 1px;

    ::placeholder {
      color: ${palette.gray5};
    }
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    background-color: ${palette.gray0};
    margin-left: 8px;
    margin-top: 1px;

    ::placeholder {
      color: ${palette.gray5};
    }
  }
`;

const buttonStyle = css`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background-color: ${palette.orange9};
  flex: 0 0 auto;

  path {
    stroke: ${palette.white};
  }

  /** mobile */
  @media (max-width: 599px) {
    background-color: transparent;
    width: 20px;
    height: 20px;

    path {
      stroke: ${palette.gray5};
    }
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    background-color: transparent;
    width: 20px;
    height: 20px;

    path {
      stroke: ${palette.gray5};
    }
  }
`;

// const listStyle = (isShow: boolean) => css`
//   display: flex;
//   opacity: ${isShow ? '0.99' : '0'};
//   pointer-events: ${isShow ? 'auto' : 'none'};
//   /* display: ${({ isShow }) => (isShow ? 'flex' : 'none')}; */
//   flex-direction: column;
//   position: absolute;
//   top: 110%;
//   width: 100%;
//   padding: 5px;
//   background: ${palette.white};
//   box-shadow: ${pixelToRem(0, 0, 2, 2)} ${palette.gray1};
//   border-radius: ${pixelToRem(10)};
//   z-index: 100;
// `;

const listStyle = (isShow: boolean) => css`
  display: flex;
  opacity: ${isShow ? '0.99' : '0'};
  pointer-events: ${isShow ? 'auto' : 'none'};
  flex-direction: column;
  position: absolute;
  top: 110%;
  width: 100%;
  padding: 5px;
  background: ${palette.white};
  box-shadow: ${pixelToRem(0, 0, 2, 2)} ${palette.gray1};
  border-radius: ${pixelToRem(10)};
  z-index: 100;
`;

const itemStyle = css`
  padding: ${pixelToRem(10)};
  box-sizing: border-box;
  border-radius: ${pixelToRem(10)};
  backface-visibility: hidden;

  :hover,
  &#isSelected {
    background: ${palette.gray1};
  }
`;

export default AutoComplete;
